import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useFormik } from "formik";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import {
  addUserType,
  fetchUserTypeById,
  updateUserType,
} from "slices/UserManagement/UserType/thunk";
import * as Yup from "yup";

const UserTypeForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { selectedUserType, loading, error } = useSelector(
    (state: any) => state.userType,
  );

  const formik = useFormik({
    initialValues: {
      user_type: "",
    },
    validationSchema: Yup.object({
      user_type: Yup.string().trim().required("User Type is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          user_type: values.user_type.trim(),
        };
        if (id) {
          const res = await dispatch(
            updateUserType({ id, data: payload }),
          ).unwrap();
        } else {
          const res = await dispatch(addUserType(payload)).unwrap();
          clearTableState(TableKeys?.USER_MANAGEMENT?.USER_TYPE);
        }

        toast.success(`User type ${id ? "updated" : "created"} successfully`);
        navigate("/userType-list");
      } catch (error: any) {
        console.log("ERROR", error);
        toast.error(error?.error || error?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  useEffect(() => {
    const fetchUserType = async () => {
      if (id && isEdit) {
        try {
          const res = await dispatch(fetchUserTypeById(id)).unwrap();
        } catch (error: any) {
          toast.error(error?.error || error?.message || "Somthing went wrong");
        }
      }
    };
    fetchUserType();
  }, [id, isEdit]);

  useEffect(() => {
    if (selectedUserType && isEdit) {
      formik.setValues({
        user_type: selectedUserType?.user_type || "",
      });
    }
  }, [selectedUserType, isEdit]);

  return (
    <>
      <PageContainer>
        <PageBreadcrumb
          title={id ? "Edit User Type" : "Add User Type"}
          titleLink="/userType-list"
          breadcrumbs={[
            { label: "User type", to: "/userType-list" },
            { label: id ? "Edit User Type" : "Add User Type", to: "" },
          ]}
          backNavigationPath={-1}
        />
        <hr />
        {isEdit && loading ? (
          <LoadingSpinner />
        ) : (
          <Form noValidate onSubmit={formik.handleSubmit}>
            <Row className="mb-4">
              {/* Department Name */}
              <Col md={6} xs={12}>
                <Form.Label className="fw-semibold">
                  User Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  id="user_type"
                  name="user_type"
                  placeholder="Enter User Type"
                  value={formik.values.user_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.user_type && !!formik.errors.user_type
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.user_type}
                </Form.Control.Feedback>
              </Col>
            </Row>

            {/* Buttons */}
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <CancelButton
                redirectTo="/userType-list"
                disabled={isSubmitting}
              />
              <PrimarySubmitButton
                label={id ? "Update" : "Submit"}
                isLoading={isSubmitting}
                loadingLabel={id ? "Updating..." : "Submitting..."}
              />
            </div>
          </Form>
        )}
      </PageContainer>
    </>
  );
};

export default UserTypeForm;
